import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Phone, Truck, Clock, User, X, Check, Trash2, Calendar, CalendarDays, List, Save, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopCalendar = () => {
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [viewMode, setViewMode] = useState('day'); // 'week', 'day', or 'list'
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [calendarTemplate, setCalendarTemplate] = useState('standard'); // Template selection
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    customer_name: '',
    phone_number: '',
    truck_info: '',
    service_type: '',
    notes: ''
  });

  // Calendar Templates - Professional configurations for different business types
  const calendarTemplates = {
    standard: {
      name: "Standard Business",
      description: "9 AM - 5 PM, Mon-Fri",
      hours: { start: 9, end: 17 },
      days: [1, 2, 3, 4, 5], // Mon-Fri
      slotDuration: 60,
      color: "blue"
    },
    service: {
      name: "Service Business",
      description: "7 AM - 6 PM, Mon-Sat",
      hours: { start: 7, end: 18 },
      days: [1, 2, 3, 4, 5, 6], // Mon-Sat
      slotDuration: 30,
      color: "green"
    },
    medical: {
      name: "Medical/Dental",
      description: "8 AM - 5 PM, Mon-Fri",
      hours: { start: 8, end: 17 },
      days: [1, 2, 3, 4, 5],
      slotDuration: 15,
      color: "teal"
    },
    salon: {
      name: "Salon/Spa",
      description: "10 AM - 8 PM, Tue-Sat",
      hours: { start: 10, end: 20 },
      days: [2, 3, 4, 5, 6], // Tue-Sat
      slotDuration: 30,
      color: "pink"
    },
    restaurant: {
      name: "Restaurant",
      description: "11 AM - 10 PM, 7 days",
      hours: { start: 11, end: 22 },
      days: [0, 1, 2, 3, 4, 5, 6], // All days
      slotDuration: 30,
      color: "orange"
    }
  };

  // Detect screen size for responsive view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setViewMode('week');
      } else {
        setViewMode(prev => prev === 'week' ? 'day' : prev);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current template settings
  const currentTemplate = calendarTemplates[calendarTemplate];
  
  // Generate time slots based on template
  const generateTimeSlots = () => {
    const slots = [];
    const { start, end } = currentTemplate.hours;
    const slotDuration = currentTemplate.slotDuration;
    
    for (let hour = start; hour < end; hour++) {
      for (let min = 0; min < 60; min += slotDuration) {
        const time24 = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const time12 = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ time: time24, display: time12 });
      }
    }
    return slots;
  };
  
  const timeSlots = generateTimeSlots();

  // Fetch calendar settings from backend on mount
  useEffect(() => {
    fetchCalendarSettings();
  }, [token]);

  useEffect(() => {
    fetchWeekSchedule();
  }, [currentDate, calendarTemplate]); // Re-fetch when template changes

  // Set selected day to today when data loads
  useEffect(() => {
    if (weekData.length > 0) {
      const todayIndex = weekData.findIndex(d => isToday(d.date));
      if (todayIndex >= 0) {
        setSelectedDayIndex(todayIndex);
      }
    }
  }, [weekData]);

  const fetchCalendarSettings = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/calendar/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.calendar_template) {
        setCalendarTemplate(res.data.calendar_template);
      }
    } catch (err) {
      console.error('Failed to fetch calendar settings:', err);
    }
  };

  const saveCalendarTemplate = async (templateKey) => {
    if (!token) {
      toast.error('Please log in to save settings');
      return;
    }
    
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/calendar/settings`, 
        { calendar_template: templateKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalendarTemplate(templateKey);
      toast.success('Calendar template saved!');
    } catch (err) {
      console.error('Failed to save calendar template:', err);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const fetchWeekSchedule = async () => {
    setLoading(true);
    try {
      const weekStart = getWeekStart(currentDate);
      const startDate = weekStart.toISOString().split('T')[0];
      const res = await axios.get(`${API}/calendar/week?start_date=${startDate}`);
      setWeekData(res.data.week || []);
    } catch (err) {
      console.error('Failed to fetch calendar:', err);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handlePrevDay = () => {
    if (selectedDayIndex > 0) {
      setSelectedDayIndex(selectedDayIndex - 1);
    } else {
      handlePrevWeek();
      setSelectedDayIndex(6);
    }
  };

  const handleNextDay = () => {
    if (selectedDayIndex < 6) {
      setSelectedDayIndex(selectedDayIndex + 1);
    } else {
      handleNextWeek();
      setSelectedDayIndex(0);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setTimeout(() => {
      const todayIndex = weekData.findIndex(d => isToday(d.date));
      if (todayIndex >= 0) {
        setSelectedDayIndex(todayIndex);
      }
    }, 100);
  };

  const handleSlotClick = (slot, date) => {
    setSelectedSlot({ ...slot, date });
    if (slot.status === 'available') {
      setBookingForm({
        customer_name: '',
        phone_number: '',
        truck_info: '',
        service_type: '',
        notes: ''
      });
      setShowBookingModal(true);
    } else if (slot.status === 'booked') {
      setBookingForm({
        customer_name: slot.customer_name || '',
        phone_number: slot.phone_number || '',
        truck_info: slot.truck_info || '',
        service_type: slot.service_type || '',
        notes: slot.notes || ''
      });
      setShowBookingModal(true);
    }
  };

  const handleBookSlot = async () => {
    if (!selectedSlot) return;
    
    try {
      await axios.post(`${API}/calendar/book`, {
        date: selectedSlot.date,
        time: selectedSlot.time,
        ...bookingForm
      });
      toast.success('Appointment booked!');
      setShowBookingModal(false);
      fetchWeekSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book slot');
    }
  };

  const handleCancelSlot = async () => {
    if (!selectedSlot?.id) return;
    
    if (window.confirm('Cancel this appointment?')) {
      try {
        await axios.delete(`${API}/calendar/slot/${selectedSlot.id}`);
        toast.success('Appointment cancelled');
        setShowBookingModal(false);
        fetchWeekSchedule();
      } catch (err) {
        toast.error('Failed to cancel');
      }
    }
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getMonthYear = () => {
    const weekStart = getWeekStart(currentDate);
    return weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isToday = (dateStr) => {
    return new Date().toISOString().split('T')[0] === dateStr;
  };

  const getSlotStyle = (status) => {
    switch (status) {
      case 'booked': 
        return 'bg-blue-50 border-blue-400 text-blue-900 shadow-sm';
      case 'completed': 
        return 'bg-green-50 border-green-400 text-green-900';
      case 'cancelled': 
        return 'bg-slate-50 border-slate-300 text-slate-500 line-through';
      case 'no-show': 
        return 'bg-red-50 border-red-300 text-red-700';
      default: 
        return 'bg-muted/30 border-border/50 text-muted-foreground hover:bg-muted/50 hover:border-primary/50';
    }
  };

  // Get all booked appointments for the sidebar/list view
  const bookedAppointments = weekData.flatMap(day => 
    (day.slots || [])
      .filter(slot => slot.status === 'booked')
      .map(slot => ({ ...slot, date: day.date, day_name: day.day_name }))
  ).sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.time.localeCompare(b.time);
  });

  // Get current day data for day view
  const currentDayData = weekData[selectedDayIndex] || null;

  return (
    <div className="min-h-screen bg-background p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-4 sm:mb-6">
          {/* Title Row */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground flex items-center gap-2 sm:gap-3">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                <span className="hidden sm:inline">CALENDAR</span>
                <span className="sm:hidden">Calendar</span>
              </h1>
              <p className="text-muted-foreground text-xs sm:text-sm mt-1">{getMonthYear()}</p>
            </div>
            
            {/* Template Selector */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Template:</span>
              <select
                value={calendarTemplate}
                onChange={(e) => saveCalendarTemplate(e.target.value)}
                disabled={savingTemplate}
                className="bg-muted border border-border rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                data-testid="calendar-template-select"
              >
                {Object.entries(calendarTemplates).map(([key, template]) => (
                  <option key={key} value={key}>{template.name}</option>
                ))}
              </select>
              {savingTemplate && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </div>
            
            {/* View Mode Toggle - Mobile Only */}
            <div className="flex lg:hidden items-center gap-1 bg-muted/50 rounded-lg p-1">
              <Button 
                variant={viewMode === 'day' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('day')}
                className="h-8 px-3"
                data-testid="view-mode-day"
              >
                <CalendarDays className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 px-3"
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Navigation Row */}
          <div className="flex items-center justify-between gap-2">
            {/* Week/Day Navigation */}
            <div className="flex items-center gap-1 sm:gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={viewMode === 'day' ? handlePrevDay : handlePrevWeek}
                className="h-9 w-9 sm:h-10 sm:w-10 p-0"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleToday}
                className="h-9 sm:h-10 px-3 sm:px-4 text-sm"
              >
                Today
              </Button>
              
              {/* Month/Year Jump */}
              <input 
                type="date"
                value={currentDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  if (e.target.value) {
                    setCurrentDate(new Date(e.target.value + 'T00:00:00'));
                  }
                }}
                className="h-9 sm:h-10 px-2 text-sm bg-muted border border-border rounded-md text-foreground cursor-pointer"
                title="Jump to date"
              />
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={viewMode === 'day' ? handleNextDay : handleNextWeek}
                className="h-9 w-9 sm:h-10 sm:w-10 p-0"
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>

            {/* Day Selector Pills - Mobile Day View */}
            {viewMode === 'day' && weekData.length > 0 && (
              <div className="flex gap-1 overflow-x-auto scrollbar-hide">
                {weekData.map((day, i) => {
                  const date = new Date(day.date + 'T00:00:00');
                  const dayNum = date.getDate();
                  const dayName = day.day_name?.slice(0, 1) || '';
                  const today = isToday(day.date);
                  const bookedCount = day.slots?.filter(s => s.status === 'booked').length || 0;
                  
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDayIndex(i)}
                      className={`flex-shrink-0 flex flex-col items-center px-2 py-1.5 rounded-lg transition-all min-w-[40px] ${
                        selectedDayIndex === i 
                          ? 'bg-primary text-primary-foreground' 
                          : today 
                            ? 'bg-primary/20 text-primary' 
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid={`day-pill-${i}`}
                    >
                      <span className="text-[10px] uppercase font-medium">{dayName}</span>
                      <span className="text-sm font-bold">{dayNum}</span>
                      {bookedCount > 0 && (
                        <span className={`text-[8px] px-1 rounded-full ${
                          selectedDayIndex === i ? 'bg-white/20' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {bookedCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Calendar Grid */}
          <div className={`${viewMode === 'list' ? 'hidden lg:block' : ''} lg:col-span-3`}>
            <Card className="card-modern overflow-hidden">
              {/* Legend */}
              <div className="p-2 sm:p-3 border-b border-border bg-muted/30 flex flex-wrap gap-3 sm:gap-4 text-xs">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-muted/50 border border-border/50"></div>
                  <span className="text-muted-foreground">Open</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-blue-500/30 border border-blue-500"></div>
                  <span className="text-muted-foreground">Booked</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-green-500/30 border border-green-500"></div>
                  <span className="text-muted-foreground">Done</span>
                </div>
              </div>

              {/* Template Info Banner */}
              <div className={`px-3 py-2 border-b border-border bg-${currentTemplate.color}-500/10 flex items-center justify-between`}>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`w-3 h-3 rounded-full bg-${currentTemplate.color}-500`}></span>
                  <span className="font-medium text-foreground">{currentTemplate.name}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">{currentTemplate.description}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {currentTemplate.slotDuration} min slots
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-64 sm:h-96">
                  <div className="spinner" />
                </div>
              ) : viewMode === 'week' ? (
                /* Week View - Desktop */
                <div className="overflow-x-auto">
                  <div className="min-w-[700px]">
                    {/* Day Headers */}
                    <div className="grid grid-cols-8 border-b border-border">
                      <div className="p-2 sm:p-3 text-center text-muted-foreground text-xs font-medium border-r border-border">
                        TIME
                      </div>
                      {weekData.map((day, i) => {
                        const date = new Date(day.date + 'T00:00:00');
                        const dayNum = date.getDate();
                        const dayName = day.day_name?.slice(0, 3) || '';
                        const today = isToday(day.date);
                        
                        return (
                          <div 
                            key={i} 
                            className={`p-2 sm:p-3 text-center border-r border-border last:border-r-0 ${today ? 'bg-primary/10' : ''}`}
                          >
                            <div className="text-xs text-muted-foreground uppercase">{dayName}</div>
                            <div className={`text-lg sm:text-xl font-bold ${today ? 'text-primary' : 'text-foreground'}`}>
                              {dayNum}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Time Slots */}
                    <div className="divide-y divide-border/50">
                      {weekData[0]?.slots?.map((_, slotIndex) => (
                        <div key={slotIndex} className="grid grid-cols-8">
                          <div className="p-1.5 sm:p-2 text-right text-muted-foreground text-xs flex items-center justify-end pr-2 sm:pr-3 border-r border-border bg-muted/20">
                            {formatTime(weekData[0].slots[slotIndex]?.time || '00:00')}
                          </div>
                          
                          {weekData.map((day, dayIndex) => {
                            const slot = day.slots[slotIndex];
                            if (!slot) return <div key={dayIndex} className="p-1 border-r border-border/30 last:border-r-0" />;
                            
                            const today = isToday(day.date);
                            
                            return (
                              <div
                                key={dayIndex}
                                onClick={() => handleSlotClick(slot, day.date)}
                                className={`p-0.5 sm:p-1 border-r border-border/30 last:border-r-0 cursor-pointer transition-all ${today ? 'bg-primary/5' : ''}`}
                                data-testid={`slot-${day.date}-${slot.time}`}
                              >
                                <div className={`h-full min-h-[36px] sm:min-h-[40px] rounded border p-0.5 sm:p-1 text-xs ${getSlotStyle(slot.status)}`}>
                                  {slot.status === 'booked' ? (
                                    <div className="truncate">
                                      <div className="font-semibold truncate text-[10px] sm:text-xs">{slot.customer_name || 'Booked'}</div>
                                      {slot.service_type && (
                                        <div className="text-[8px] sm:text-[10px] opacity-75 truncate">{slot.service_type}</div>
                                      )}
                                    </div>
                                  ) : slot.status === 'available' ? (
                                    <div className="h-full flex items-center justify-center opacity-30 hover:opacity-60">
                                      <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </div>
                                  ) : (
                                    <div className="truncate opacity-50 text-[10px]">{slot.status}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Day View - Mobile/Tablet Friendly */
                currentDayData && (
                  <div className="p-2 sm:p-4">
                    {/* Day Header */}
                    <div className="text-center mb-4 pb-3 border-b border-border">
                      <div className="text-muted-foreground text-sm uppercase">{currentDayData.day_name}</div>
                      <div className={`text-3xl font-bold ${isToday(currentDayData.date) ? 'text-primary' : 'text-foreground'}`}>
                        {new Date(currentDayData.date + 'T00:00:00').getDate()}
                      </div>
                      {isToday(currentDayData.date) && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Today</span>
                      )}
                    </div>

                    {/* Time Slots - Larger Touch Targets */}
                    <div className="space-y-2">
                      {currentDayData.slots?.map((slot, i) => (
                        <div
                          key={i}
                          onClick={() => handleSlotClick(slot, currentDayData.date)}
                          className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border cursor-pointer transition-all active:scale-[0.98] ${getSlotStyle(slot.status)}`}
                          data-testid={`day-slot-${slot.time}`}
                        >
                          {/* Time */}
                          <div className="text-center min-w-[60px]">
                            <div className="text-sm sm:text-base font-bold">{formatTime(slot.time)}</div>
                          </div>

                          {/* Divider */}
                          <div className="w-px h-10 bg-border/50"></div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {slot.status === 'booked' ? (
                              <>
                                <div className="font-semibold text-sm sm:text-base truncate">{slot.customer_name || 'Booked'}</div>
                                {slot.service_type && (
                                  <div className="text-xs sm:text-sm opacity-75 truncate">{slot.service_type}</div>
                                )}
                                {slot.phone_number && (
                                  <div className="text-xs opacity-60 flex items-center gap-1 mt-1">
                                    <Phone className="w-3 h-3" /> {slot.phone_number}
                                  </div>
                                )}
                              </>
                            ) : slot.status === 'available' ? (
                              <div className="flex items-center gap-2 opacity-50">
                                <Plus className="w-4 h-4" />
                                <span className="text-sm">Tap to book</span>
                              </div>
                            ) : (
                              <div className="opacity-50 capitalize">{slot.status}</div>
                            )}
                          </div>

                          {/* Status indicator */}
                          {slot.status === 'booked' && (
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </Card>
          </div>

          {/* Sidebar - Upcoming Appointments / List View */}
          <div className={`${viewMode !== 'list' ? 'hidden lg:block' : ''} lg:col-span-1`}>
            <Card className="card-modern lg:sticky lg:top-4">
              <div className="p-3 sm:p-4 border-b border-border">
                <h2 className="font-bold text-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  This Week
                </h2>
                <p className="text-xs text-muted-foreground">{bookedAppointments.length} appointments</p>
              </div>
              
              <div className="max-h-[60vh] lg:max-h-[500px] overflow-y-auto">
                {bookedAppointments.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No appointments</p>
                    <p className="text-xs mt-1">Tap a time slot to book</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {bookedAppointments.map((apt, i) => {
                      const date = new Date(apt.date + 'T00:00:00');
                      const dayName = apt.day_name?.slice(0, 3) || '';
                      const dayNum = date.getDate();
                      const today = isToday(apt.date);
                      
                      return (
                        <div 
                          key={i} 
                          className={`p-3 sm:p-4 hover:bg-muted/30 cursor-pointer transition-colors active:bg-muted/50 ${today ? 'bg-primary/5' : ''}`}
                          onClick={() => {
                            handleSlotClick(apt, apt.date);
                            // On mobile, also navigate to that day
                            if (viewMode !== 'week') {
                              const dayIndex = weekData.findIndex(d => d.date === apt.date);
                              if (dayIndex >= 0) {
                                setSelectedDayIndex(dayIndex);
                                setViewMode('day');
                              }
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`text-center min-w-[44px] py-1 px-2 rounded-lg ${today ? 'bg-primary/20 text-primary' : 'bg-muted/50'}`}>
                              <div className="text-[10px] text-muted-foreground uppercase">{dayName}</div>
                              <div className="text-lg font-bold">{dayNum}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-foreground truncate">{apt.customer_name || 'Customer'}</div>
                              <div className="text-sm text-blue-400 font-medium">
                                {formatTime(apt.time)}
                              </div>
                              {apt.service_type && (
                                <div className="text-xs text-muted-foreground truncate mt-1">
                                  {apt.service_type}
                                </div>
                              )}
                              {apt.phone_number && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" /> {apt.phone_number}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Booking Modal - Mobile Optimized */}
        {showBookingModal && selectedSlot && (
          <div 
            className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50"
            onClick={() => setShowBookingModal(false)}
          >
            <Card 
              className="card-modern w-full sm:max-w-md max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Handle - Mobile */}
              <div className="sm:hidden flex justify-center py-2">
                <div className="w-10 h-1 bg-muted-foreground/30 rounded-full"></div>
              </div>

              <div className="p-4 border-b border-border bg-muted/30">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-foreground">
                      {selectedSlot.status === 'available' ? 'Book Appointment' : 'Appointment Details'}
                    </h2>
                    <p className="text-sm text-primary font-medium mt-1">
                      {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {formatTime(selectedSlot.time)}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowBookingModal(false)} className="hidden sm:flex">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto max-h-[50vh]">
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 flex items-center gap-2">
                    <User className="w-4 h-4" /> Customer Name
                  </label>
                  <Input
                    data-testid="booking-name"
                    value={bookingForm.customer_name}
                    onChange={(e) => setBookingForm(f => ({ ...f, customer_name: e.target.value }))}
                    placeholder="John Smith"
                    className="bg-muted border-border h-11"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone Number
                  </label>
                  <Input
                    data-testid="booking-phone"
                    type="tel"
                    value={bookingForm.phone_number}
                    onChange={(e) => setBookingForm(f => ({ ...f, phone_number: e.target.value }))}
                    placeholder="+1 555-123-4567"
                    className="bg-muted border-border h-11"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Vehicle Info
                  </label>
                  <Input
                    data-testid="booking-truck"
                    value={bookingForm.truck_info}
                    onChange={(e) => setBookingForm(f => ({ ...f, truck_info: e.target.value }))}
                    placeholder="2020 Freightliner Cascadia"
                    className="bg-muted border-border h-11"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Service Needed
                  </label>
                  <Input
                    data-testid="booking-service"
                    value={bookingForm.service_type}
                    onChange={(e) => setBookingForm(f => ({ ...f, service_type: e.target.value }))}
                    placeholder="Oil change, diagnostics, brakes..."
                    className="bg-muted border-border h-11"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5">Notes</label>
                  <Input
                    data-testid="booking-notes"
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Additional details..."
                    className="bg-muted border-border h-11"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-border bg-muted/20 flex gap-2 safe-area-bottom">
                {selectedSlot.status === 'booked' && selectedSlot.id && (
                  <Button 
                    variant="destructive" 
                    onClick={handleCancelSlot}
                    className="flex-1 h-12"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                )}
                <Button 
                  onClick={handleBookSlot}
                  className="flex-1 h-12 bg-primary hover:bg-primary/90"
                  data-testid="confirm-booking-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {selectedSlot.status === 'available' ? 'Book' : 'Update'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopCalendar;
